import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Search, UserCheck, Building2, LogIn, AlertTriangle, ChevronRight } from "lucide-react";
import { showError } from "@/lib/toastHelpers";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AdminUser {
  _id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  createdAt?: string;
  isAdmin?: boolean;
}

interface Workspace {
  _id: string;
  name: string;
  type?: string;
  plan?: string;
}

// ─── API helpers ──────────────────────────────────────────────────────────────

async function fetchUsers(search: string): Promise<AdminUser[]> {
  const params = new URLSearchParams({ search, limit: "20" });
  const data = await apiRequest("GET", `/api/admin/users?${params}`) as any;
  return data.users ?? data ?? [];
}

async function fetchWorkspaces(userId: string): Promise<Workspace[]> {
  const data = await apiRequest("GET", `/api/admin/impersonate/workspaces/${userId}`) as any;
  return data.workspaces ?? [];
}

async function startImpersonation(userId: string, workspaceId: string) {
  return apiRequest("POST", "/api/admin/impersonate", { userId, workspaceId }) as unknown as Promise<{ token: string }>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Impersonate() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Debounce search input
  const handleSearchChange = (value: string) => {
    setSearch(value);
    const t = setTimeout(() => setDebouncedSearch(value), 400);
    return () => clearTimeout(t);
  };

  // ── Queries ──
  const {
    data: users = [],
    isLoading: usersLoading,
    isFetching: usersFetching,
  } = useQuery({
    queryKey: ["admin-users-search", debouncedSearch],
    queryFn: () => fetchUsers(debouncedSearch),
    enabled: debouncedSearch.length >= 2,
  });

  const {
    data: workspaces = [],
    isLoading: workspacesLoading,
  } = useQuery({
    queryKey: ["user-workspaces", selectedUser?._id],
    queryFn: () => fetchWorkspaces(selectedUser!._id),
    enabled: !!selectedUser,
  });

  // ── Impersonation mutation ──
  const impersonateMutation = useMutation({
    mutationFn: () => startImpersonation(selectedUser!._id, selectedWorkspace!._id),
    onSuccess: (data) => {
      const mainAppUrl = import.meta.env.VITE_MAIN_APP_URL || window.location.origin;
      const url = `${mainAppUrl}?impersonate_token=${encodeURIComponent(data.token)}&workspace_id=${encodeURIComponent(selectedWorkspace!._id)}`;
      window.open(url, "_blank");
      setConfirmOpen(false);
    },
    onError: (err: Error) => {
      showError(err.message ?? "Failed to start impersonation");
      setConfirmOpen(false);
    },
  });

  const handleSelectUser = (user: AdminUser) => {
    setSelectedUser(user);
    setSelectedWorkspace(null);
  };

  const handleImpersonate = () => {
    if (!selectedUser || !selectedWorkspace) return;
    setConfirmOpen(true);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Impersonate User</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Select a user and workspace to view the app from their perspective. A
          banner will be shown and the session expires after 1 hour.
        </p>
      </div>

      <Alert className="border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Impersonation sessions are logged. All actions taken during impersonation
          are performed as the selected user and are visible to them.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Step 1: Pick a user ── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                1
              </span>
              Select a User
            </CardTitle>
            <CardDescription>Search by name or email</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users…"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>

            {debouncedSearch.length < 2 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Type at least 2 characters to search
              </p>
            )}

            {(usersLoading || usersFetching) && (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            )}

            {!usersLoading && !usersFetching && debouncedSearch.length >= 2 && users.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No users found</p>
            )}

            {users.length > 0 && (
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead className="w-[80px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => {
                      const isSelected = selectedUser?._id === user._id;
                      return (
                        <TableRow
                          key={user._id}
                          className={`cursor-pointer ${isSelected ? "bg-primary/5 border-l-2 border-l-primary" : "hover:bg-muted/50"}`}
                          onClick={() => handleSelectUser(user)}
                        >
                          <TableCell>
                            <div className="font-medium text-sm">
                              {user.firstName || user.lastName
                                ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
                                : user.email}
                            </div>
                            <div className="text-xs text-muted-foreground">{user.email}</div>
                          </TableCell>
                          <TableCell>
                            {isSelected ? (
                              <UserCheck className="h-4 w-4 text-primary" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Step 2: Pick a workspace ── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                2
              </span>
              Select a Workspace
            </CardTitle>
            <CardDescription>
              {selectedUser
                ? `Workspaces accessible by ${selectedUser.firstName ?? selectedUser.email}`
                : "Select a user first"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {!selectedUser && (
              <p className="text-sm text-muted-foreground text-center py-8">
                Choose a user on the left to see their workspaces
              </p>
            )}

            {selectedUser && workspacesLoading && (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            )}

            {selectedUser && !workspacesLoading && workspaces.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                No workspaces found for this user
              </p>
            )}

            {workspaces.length > 0 && (
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Workspace</TableHead>
                      <TableHead className="w-[80px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workspaces.map((ws) => {
                      const isSelected = selectedWorkspace?._id === ws._id;
                      return (
                        <TableRow
                          key={ws._id}
                          className={`cursor-pointer ${isSelected ? "bg-primary/5 border-l-2 border-l-primary" : "hover:bg-muted/50"}`}
                          onClick={() => setSelectedWorkspace(ws)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                              <div>
                                <div className="font-medium text-sm">{ws.name}</div>
                                {ws.type && (
                                  <Badge variant="outline" className="text-[10px] h-4 px-1 capitalize mt-0.5">
                                    {ws.type}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {isSelected ? (
                              <UserCheck className="h-4 w-4 text-primary" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Impersonate button */}
            {selectedUser && selectedWorkspace && (
              <div className="pt-2">
                <Button
                  className="w-full gap-2"
                  onClick={handleImpersonate}
                  disabled={impersonateMutation.isPending}
                >
                  <LogIn className="h-4 w-4" />
                  Impersonate {selectedUser.firstName ?? selectedUser.email} in{" "}
                  {selectedWorkspace.name}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Confirmation dialog ── */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogIn className="h-5 w-5 text-primary" />
              Confirm Impersonation
            </DialogTitle>
            <DialogDescription>
              You are about to open the app as{" "}
              <strong>
                {selectedUser?.firstName
                  ? `${selectedUser.firstName} ${selectedUser.lastName ?? ""}`.trim()
                  : selectedUser?.email}
              </strong>{" "}
              in workspace <strong>{selectedWorkspace?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md bg-muted p-3 text-sm space-y-1">
            <div className="text-muted-foreground">This action will:</div>
            <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
              <li>Open a new tab logged in as the selected user</li>
              <li>Show an impersonation banner with an exit option</li>
              <li>Expire automatically after 1 hour</li>
              <li>Be logged for audit purposes</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => impersonateMutation.mutate()}
              disabled={impersonateMutation.isPending}
              className="gap-2"
            >
              <LogIn className="h-4 w-4" />
              {impersonateMutation.isPending ? "Opening…" : "Start Impersonation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
